<template>
	<Modal
		v-model:open="isOpen"
		:show-close-icon="false"
		prevent-interact-outside
		content-class="h-full max-w-screen max-h-screen flex flex-col bg-white"
		header-class="font-matter-sq text-xl md:text-2xl font-normal leading-[1.875rem] z-10 tracking-[0.02em] text-left text-primary justify-start items-start h-min py-[17px] px-5 md:py-[17px] md:px-5"
		footer-class="hidden"
	>
		<template #header>
			<Container class="!py-0 px-0">
				<DialogTitle class="flex size-full items-center md:pb-0">
					<div
						class="relative flex w-full items-start justify-start md:flex-nowrap md:items-center md:justify-center"
					>
						<div
							class="order-1 flex w-1/2 items-center justify-between md:order-1 md:w-auto md:px-2"
						>
							<TextLink
								:element="'a'"
								class="flex items-center gap-2"
								@click="handleGoBack"
							>
								<ArrowLeftIcon class="size-5" />
								<span class="text-base font-normal">{{
									strings.backButton
								}}</span>
							</TextLink>
						</div>

						<div
							class="order-2 flex grow justify-center px-10 font-matter-sq text-[18px] font-normal leading-[27px]"
						>
							{{ strings.title }}
						</div>

						<div class="order-3 block w-1/2 md:hidden" />
					</div>
				</DialogTitle>
			</Container>
		</template>

		<template #default>
			<Container
				ref="modalScrollContainer"
				class="flex grow overflow-y-auto !p-0 md:!px-5 md:!py-0"
			>
				<SimilarPropertiesList
					:properties="properties"
					:is-loading="isLoading"
					:has-more="hasMore"
					:is-empty="isEmpty"
					:base-property="baseProperty"
					:scroll-container="modalScrollContainer"
					@load-more="loadNextPage"
				/>
			</Container>
		</template>
	</Modal>
</template>

<script setup lang="ts">
import type { Property } from '~/core/entities/property';

import { ArrowLeftIcon } from '@radix-icons/vue';
import { useSimilarProperties } from '~/composables/useSimilarProperties';

import { Container } from '../Container';
import { Modal } from '../Modal';
import { DialogTitle } from '../ui/dialog';
import { TextLink } from '../ui/text-link';
import SimilarPropertiesList from './SimilarPropertiesList.vue';
import messages from './SimilarPropertiesModal.locale.json';

interface SimilarPropertiesModalProps {
	isOpen: boolean;
	baseProperty: Property;
}

const strings = messages.pt;

const props = defineProps<SimilarPropertiesModalProps>();

const emit = defineEmits<{
	'update:is-open': [value: boolean];
}>();

const modalScrollContainer = ref<HTMLElement | null>(null);

const basePropertyRef = computed(() => props.baseProperty);
const {
	properties,
	isLoading,
	hasMore,
	isEmpty,
	loadSimilarProperties,
	loadNextPage
} = useSimilarProperties(basePropertyRef);

const isOpen = computed({
	get: () => props.isOpen,
	set: (value: boolean) => emit('update:is-open', value)
});

function handleGoBack(): void {
	isOpen.value = false;
}

// Load similar properties when modal opens
watch(
	() => props.isOpen,
	(newValue: boolean) => {
		if (newValue && props.baseProperty) {
			loadSimilarProperties();
		}
	},
	{ immediate: true }
);
</script>
